/**
 * Fuel Management API service
 */

import api from '../api/client';
import { apiGet } from '@/services/api/baseService';

const base = '/api/fuel-management';

export interface InvoiceSummary {
  invoice_number: string;
  invoice_date: string | null;
  transaction_count: number;
  duplicate_count?: number;
  total_excl_vat: number;
  total_vat: number;
  total_incl_vat: number;
}

export interface FuelTransaction {
  fuel_id: number;
  purchased: string;
  station_name: string | null;
  product_name: string | null;
  quantity: number;
  price_excl_vat: number;
  vat: number;
  card_number: string | null;
  customer_number: string | null;
  fuel_company_name?: string | null;
  source_station_company?: string | null;
  cross_source_duplicate?: boolean;
  duplicate_of_fuel_id?: number | null;
  original_company_name?: string | null;
}

export interface FuelCard {
  card_number: string;
  embossed_number?: string | null;
  card_name?: string | null;
  pin_code: string | null;
  fuel_company_id?: number | null;
  fuel_company_name: string | null;
  vehicle_id: number | null;
  registration_number: string | null;
  valid_from: string | null;
  is_active: boolean;
}

export interface CardHistoryEntry {
  id: number;
  card_number: string;
  embossed_number?: string | null;
  card_name: string | null;
  pin_code: string | null;
  vehicle_id: number | null;
  registration_number: string | null;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean;
  created_at: string;
  fuel_company_id?: number | null;
  fuel_company_name: string | null;
}

// ── Invoice endpoints ──────────────────────────────────────────────────────

export async function getInvoices(dateFrom?: string, dateTo?: string): Promise<InvoiceSummary[]> {
  const params = new URLSearchParams();
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  const qs = params.toString();
  const data = await apiGet<InvoiceSummary[]>(`${base}/invoices${qs ? `?${qs}` : ''}`);
  return data ?? [];
}

export async function getInvoiceTransactions(invoiceNumber: string): Promise<FuelTransaction[]> {
  const data = await apiGet<FuelTransaction[]>(
    `${base}/invoices/${encodeURIComponent(invoiceNumber)}/transactions`
  );
  return data ?? [];
}

// ── Card endpoints ─────────────────────────────────────────────────────────

export async function getCards(): Promise<FuelCard[]> {
  const data = await apiGet<FuelCard[]>(`${base}/cards`);
  return data ?? [];
}

export async function linkCard(
  cardNumber: string,
  vehicleId: number,
  validFrom: string,
  fuelCompanyId?: number,
  pinCode?: string,
  cardName?: string,
  embossedNumber?: string
): Promise<void> {
  await api.post(`${base}/cards/${encodeURIComponent(cardNumber)}/link`, {
    vehicleId,
    validFrom,
    fuelCompanyId,
    pinCode,
    cardName,
    embossedNumber,
  });
}

export async function unlinkCard(cardNumber: string, validTo?: string): Promise<void> {
  await api.post(`${base}/cards/${encodeURIComponent(cardNumber)}/unlink`, {
    validTo,
  });
}

export async function getCardHistory(cardNumber: string): Promise<CardHistoryEntry[]> {
  const data = await apiGet<CardHistoryEntry[]>(
    `${base}/cards/${encodeURIComponent(cardNumber)}/history`
  );
  return data ?? [];
}

// ── Vehicle-specific endpoints ─────────────────────────────────────────────

export async function getVehicleCards(vehicleId: number): Promise<CardHistoryEntry[]> {
  const data = await apiGet<CardHistoryEntry[]>(`${base}/vehicles/${vehicleId}/cards`);
  return data ?? [];
}

export async function getVehicleTransactions(
  vehicleId: number,
  dateFrom?: string,
  dateTo?: string
): Promise<FuelTransaction[]> {
  const params = new URLSearchParams();
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  const qs = params.toString();
  const data = await apiGet<FuelTransaction[]>(
    `${base}/vehicles/${vehicleId}/transactions${qs ? `?${qs}` : ''}`
  );
  return data ?? [];
}
